import { useCallback, useId, useRef, useState } from 'react';
import {
  closeImageDialog$,
  imageDialogState$,
  saveImage$,
  useCellValues,
  usePublisher,
} from '@mdxeditor/editor';
import { Button, Dialog } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IMarkdownEditorImageFormValues } from './MarkdownEditorImageForm';
import { useApi } from '~/lib/api';
import { MarkdownEditorImageForm } from './MarkdownEditorImageForm';

type IMarkdownEditorImageDialogProps = {
  appId?: string;
};

const styles = stylex.create({
  dialog: {
    minWidth: 560,
  },
});

export const MarkdownEditorImageDialog: React.FC<
  IMarkdownEditorImageDialogProps
> = (props) => {
  const { appId } = props;

  const api = useApi();
  const { t } = useTranslation();

  const [imageDialogState] = useCellValues(imageDialogState$);
  const saveImage = usePublisher(saveImage$);
  const closeImageDialog = usePublisher(closeImageDialog$);
  const [submitting, setSubmitting] = useState(false);

  const currentMode = useRef<'new' | 'editing'>();
  if (imageDialogState.type !== 'inactive') {
    currentMode.current = imageDialogState.type;
  }

  const handleClose = useCallback((): void => {
    // TODO cancel uploads (properly) in progress if any, then close the dialog
    closeImageDialog();
  }, [closeImageDialog]);

  const handleSubmit = useCallback(
    async (values: IMarkdownEditorImageFormValues): Promise<void> => {
      setSubmitting(true);

      try {
        let src: string | undefined;
        let title: string | undefined;
        let altText: string | undefined;

        if (imageDialogState.type === 'new') {
          const insertImageFile = values.files[0];
          if (!insertImageFile?.id) {
            throw new Error('Could not insert the image: missing picture id');
          }
          /*
            TODO: should the file uploader directly return the document associated
            to the uploaded file?
          */
          const picture = await api.pictures.get(appId, insertImageFile.id);
          src = picture.mediumUrl;
          title = values.title;
          altText = values.alt;
        } else if (imageDialogState.type === 'editing') {
          const replacingImageFile = values.files[0];
          if (replacingImageFile?.id) {
            const picture = await api.pictures.get(
              appId,
              replacingImageFile.id,
            );
            src = picture.mediumUrl;
          } else {
            src = imageDialogState.initialValues.src;
          }
          title =
            values.title && values.title !== ''
              ? values.title
              : imageDialogState.initialValues.title;
          altText =
            values.alt && values.alt !== ''
              ? values.alt
              : imageDialogState.initialValues.altText;
        }

        saveImage({
          src,
          title,
          altText,
          // use a fake FileList: by using this component, we bypass the original mdxEditor behavior
          file: new DataTransfer().files,
        });
      } finally {
        setSubmitting(false);
      }
    },
    [api, appId, imageDialogState, saveImage],
  );

  const formId = useId();
  const opened = imageDialogState.type !== 'inactive';

  return (
    <Dialog
      headline={
        currentMode.current === 'new'
          ? t('MarkdownEditorImageDialog.title.insert')
          : t('MarkdownEditorImageDialog.title.update')
      }
      sx={styles.dialog}
      opened={opened}
      onClose={handleClose}
      actions={({ close }) => (
        <>
          <Button variant="text" onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={submitting} form={formId}>
            {currentMode.current === 'new'
              ? t('MarkdownEditorImageDialog.actions.insert')
              : t('MarkdownEditorImageDialog.actions.update')}
          </Button>
        </>
      )}
    >
      <MarkdownEditorImageForm
        id={formId}
        onSubmit={handleSubmit}
        appId={appId}
        hideActionBar
        defaultValues={
          imageDialogState.type === 'editing'
            ? {
                src: imageDialogState.initialValues.src,
                title: imageDialogState.initialValues.title,
                alt: imageDialogState.initialValues.altText,
                files: [],
              }
            : undefined
        }
        extended
      />
    </Dialog>
  );
};
